/**
 * Time utility functions for formatting and displaying timestamps
 * Uses Luxon for reliable date/time handling
 */
import { DateTime } from 'luxon';
import * as React from 'react';

/**
 * Formats a timestamp as relative time (e.g., "2 minutes ago", "3 hours ago", "just now")
 * Uses Luxon's toRelative() for accurate relative time formatting
 *
 * @param timestamp - ISO string or timestamp in milliseconds
 * @returns Formatted relative time string
 *
 * @example
 * ```typescript
 * formatRelativeTime('2024-01-15T10:30:00Z') // "2 minutes ago"
 * formatRelativeTime(Date.now() - 3600000) // "1 hour ago"
 * formatRelativeTime(Date.now() - 30000) // "30 seconds ago"
 * ```
 */
export function formatRelativeTime(timestamp: string | number): string {
  let dateTime: DateTime;

  if (typeof timestamp === 'string') {
    dateTime = DateTime.fromISO(timestamp);
  } else {
    dateTime = DateTime.fromMillis(timestamp);
  }

  // Check if the date is valid
  if (!dateTime.isValid) {
    return 'Invalid date';
  }

  // Use Luxon's built-in relative time formatting
  const relative = dateTime.toRelative({ base: DateTime.now() });

  // Customize "in X seconds" to "just now" for very recent timestamps
  if (relative && relative.includes('second')) {
    return 'just now';
  }

  return relative || 'just now';
}

/**
 * Formats a timestamp as a short relative time (e.g., "2m", "3h", "4d")
 * Useful for compact displays
 *
 * @param timestamp - ISO string or timestamp in milliseconds
 * @returns Short formatted relative time string
 *
 * @example
 * ```typescript
 * formatShortRelativeTime('2024-01-15T10:30:00Z') // "2m"
 * formatShortRelativeTime(Date.now() - 3600000) // "1h"
 * ```
 */
export function formatShortRelativeTime(timestamp: string | number): string {
  let dateTime: DateTime;

  if (typeof timestamp === 'string') {
    dateTime = DateTime.fromISO(timestamp);
  } else {
    dateTime = DateTime.fromMillis(timestamp);
  }

  if (!dateTime.isValid) {
    return 'N/A';
  }

  const now = DateTime.now();
  const diff = now.diff(dateTime, ['years', 'months', 'days', 'hours', 'minutes', 'seconds']);

  if (diff.years >= 1) {
    return `${Math.floor(diff.years)}y`;
  }
  if (diff.months >= 1) {
    return `${Math.floor(diff.months)}mo`;
  }
  if (diff.days >= 1) {
    return `${Math.floor(diff.days)}d`;
  }
  if (diff.hours >= 1) {
    return `${Math.floor(diff.hours)}h`;
  }
  if (diff.minutes >= 1) {
    return `${Math.floor(diff.minutes)}m`;
  }

  return 'now';
}

/**
 * Hook to get relative time that updates automatically
 * Useful for real-time updates in components
 *
 * @param timestamp - ISO string or timestamp in milliseconds
 * @param updateInterval - How often to update in milliseconds (default: 60000 = 1 minute)
 * @returns Current relative time string
 *
 * @example
 * ```typescript
 * function NotificationTime({ timestamp }: { timestamp: string }) {
 *   const relativeTime = useRelativeTime(timestamp);
 *   return <span>{relativeTime}</span>;
 * }
 * ```
 */
export function useRelativeTime(timestamp: string | number, updateInterval: number = 60000): string {
  const [relativeTime, setRelativeTime] = React.useState(() => formatRelativeTime(timestamp));

  React.useEffect(() => {
    // Update immediately
    setRelativeTime(formatRelativeTime(timestamp));

    // Set up interval for updates
    const interval = setInterval(() => {
      setRelativeTime(formatRelativeTime(timestamp));
    }, updateInterval);

    return () => clearInterval(interval);
  }, [timestamp, updateInterval]);

  return relativeTime;
}

/*
 * Calendar dates (YYYY-MM-DD): a day with no time and no timezone, read in UTC so
 * arithmetic never crosses a DST change.
 */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;
const calendarDay = (date: string) => DateTime.fromISO(date, { zone: 'utc' });

export function isCalendarDate(value: unknown): value is string {
  return typeof value === 'string' && CALENDAR_DATE.test(value) && calendarDay(value).isValid;
}

export function addCalendarDays(date: string, days: number): string {
  return calendarDay(date).plus({ days }).toISODate()!;
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function calendarDaysBetween(from: string, to: string): number {
  return Math.round(calendarDay(to).diff(calendarDay(from), 'days').days);
}

/** The Monday of the week containing `date`. */
export function startOfCalendarWeek(date: string): string {
  return calendarDay(date).startOf('week').toISODate()!;
}

export function formatCalendarDay(date: string, locale: string, options: Intl.DateTimeFormatOptions): string {
  return calendarDay(date).setLocale(locale).toLocaleString(options);
}

/** The city an IANA timezone is named after: 'Europe/Paris' → 'Paris', 'America/Port_of_Spain' → 'Port of Spain'. */
export function timezoneCity(timeZone: string): string {
  return (timeZone.split('/').pop() ?? timeZone).replace(/_/g, ' ');
}
